import { useState, useEffect, type JSX } from 'react';
import LeftImageRightText from '@/components/LeftImageRightText';
import LeftTextInsideImage from '@/components/LeftTextInsideImage';
import RightTextInsideImage from '@/components/RightTextInsideImage';
import LeftTextRightImage from '@/components/LeftTextRightImage';
import LeftTextBottomRightImage from '@/components/LeftTextBottomRightImage';
import TwoTextColumns from '@/components/TwoTextColumns';
import TextImage from '@/components/TextImage';
import { getPageById } from '@/libs/sanity/client';
import TopContentMediaBottom from '@/components/TopContentMediaBottom';
import LeftTextRightImageAndVideo from '@/components/LeftTextRightImageAndVideo';
import ContentSlideAndImage from '@/components/ContentSlideAndImage';
import TabList from './TabList';
import TextImageAndPopupVideo from '@/components/TextImageAndPopupVideo';
import Content2PartsSlideAndImage from '@/components/Content2PartsSlideAndImage';
import Content2PartsSlideAndBottomImage from './Content2PartsSlideAndBottomImage';
import HeroPartner from './PartnerPortal/HeroPartner';
import LeftTextRightImagePartner from './PartnerPortal/LeftTextRightImagePartner';
import BecomeOurChannelPartner from './PartnerPortal/becomeOurChannelPartner';
import PartnerNetwork from './PartnerPortal/Partner Network Homepage';
import PartnerExclusiveVAR from './PartnerPortal/ExclusiveVAR';
import ChannelPartnerPrograms from './PartnerPortal/ChannelPartnersHomepage';
import PartnerAuthorisedVAR from './PartnerPortal/AuthorisedVAR';
import PreferredVAR from './PartnerPortal/PreferredVAR';
import ProfessionalServicesProviders from './PartnerPortal/ProfessionalServicesProviders';

const GeneralComponent = (
  sanityModuleData: any,
  isNewsAndEventsPage: boolean = false
) => {
  const [moduleData, setModuleData] = useState(sanityModuleData);
  const [isLoading, setIsLoading] = useState(true);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!moduleData) {
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      const { _type, readMoreLink } = moduleData;

      if (_type === 'leftTextRightImage' && readMoreLink?.internal) {
        const ref = await getPageById(readMoreLink.internal?._ref);
        setModuleData((prevData: typeof moduleData) => ({
          ...prevData,
          readMoreLink: {
            ...prevData.readMoreLink,
            internal: { ...prevData.readMoreLink.internal, ref },
          },
        }));
      }

      if (_type === 'leftTextRightImageAndVideo') {
        if (moduleData.video?.learnMore?.internal) {
          const ref = await getPageById(
            moduleData.video.learnMore.internal?._ref
          );
          setModuleData((prevData: typeof moduleData) => ({
            ...prevData,
            video: {
              ...prevData.video,
              learnMore: {
                ...prevData.video.learnMore,
                internal: { ...prevData.video.learnMore.internal, ref },
              },
            },
          }));
        }
        if (readMoreLink?.internal) {
          const ref = await getPageById(readMoreLink.internal?._ref);
          setModuleData((prevData: typeof moduleData) => ({
            ...prevData,
            readMoreLink: {
              ...prevData.readMoreLink,
              internal: { ...prevData.readMoreLink.internal, ref },
            },
          }));
        }
      }

      if (_type === 'contentSlideAndImage') {
        setModuleData((prevData: typeof moduleData) => ({
          ...prevData,
          cardSlides: prevData.cardSlides?.map((slide: any, index: number) => ({
            ...slide,
            id: index + 1,
            isActive: index === 0,
          })),
        }));
      }
      if (
        _type === 'content2PartsSlideAndImage' ||
        _type === 'content2PartsSlideAndBottomImage'
      ) {
        setModuleData((prevData: typeof moduleData) => ({
          ...prevData,
          cardSlides2Parts: prevData.cardSlides2Parts?.map(
            (slide: any, index: number) => ({
              ...slide,
              id: index + 1,
              isActive: index === 0,
            })
          ),
        }));
      }
      setIsLoading(false);
    };

    fetchData();
  }, []);

  if (isLoading) return <div>Loading...</div>;

  // eslint-disable-next-line no-undef
  const components: { [key: string]: JSX.Element } = {
    leftImageRightText: <LeftImageRightText {...moduleData} />,
    leftTextRightImage: <LeftTextRightImage {...moduleData} />,
    leftTextRightImageAndVideo: <LeftTextRightImageAndVideo {...moduleData} />,
    leftTextBottomRightImage: <LeftTextBottomRightImage {...moduleData} />,
    leftTextInsideImage: <LeftTextInsideImage {...moduleData} />,
    rightTextInsideImage: <RightTextInsideImage {...moduleData} />,
    topContentMediaBottom: <TopContentMediaBottom {...moduleData} />,
    contentSlideAndImage: <ContentSlideAndImage {...moduleData} />,
    twoTextColumns: <TwoTextColumns {...moduleData} />,
    textImage: <TextImage {...moduleData} />,
    textImageAndPopupVideo: <TextImageAndPopupVideo {...moduleData} />,
    tabList: <TabList {...moduleData} />,
    content2PartsSlideAndImage: <Content2PartsSlideAndImage {...moduleData} />,
    content2PartsSlideAndBottomImage: (
      <Content2PartsSlideAndBottomImage {...moduleData} />
    ),
    heroPartner: <HeroPartner {...moduleData} />,
    leftTextRightImagePartner: <LeftTextRightImagePartner {...moduleData} />,
    becomeOurChannelPartner: <BecomeOurChannelPartner {...moduleData} />,
    partnerNetworkTables: <PartnerNetwork {...moduleData} />,
    partnerExclusiveVARTables: <PartnerExclusiveVAR {...moduleData} />,
    channelPartnerProgramsTables: <ChannelPartnerPrograms {...moduleData} />,
    partnerAuthorisedVARTables: <PartnerAuthorisedVAR {...moduleData} />,
    partnerPreferredVARTables: <PreferredVAR {...moduleData} />,
    partnerProfessionalServicesProviders: (
      <ProfessionalServicesProviders {...moduleData} />
    ),
  };

  return moduleData._type ? components[moduleData._type] : null;
};

export default GeneralComponent;
